import { format, parseISO } from 'date-fns';
import type { ThreatTier } from '@/types';

export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    return format(parseISO(dateString), 'MMM d, yyyy');
  } catch {
    return dateString;
  }
}

export function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    return format(parseISO(dateString), 'MMM d, yyyy h:mm a');
  } catch {
    return dateString;
  }
}

export function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatNumber(value: number | null | undefined, decimals = 0): string {
  if (value == null) return '-';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null) return '-';
  return `${(value * 100).toFixed(1)}%`;
}

export function getThreatTierLabel(tier: ThreatTier): string {
  const labels: Record<ThreatTier, string> = {
    1: 'CRITICAL',
    2: 'HIGH',
    3: 'MODERATE',
    4: 'LOW',
    5: 'MINIMAL',
  };
  return labels[tier];
}

export function getThreatTierColor(tier: ThreatTier): string {
  // Monochrome styling - using different shades
  const colors: Record<ThreatTier, string> = {
    1: 'bg-mono-100 text-mono-950 border border-mono-100',
    2: 'bg-mono-300 text-mono-950 border border-mono-300',
    3: 'bg-mono-500 text-mono-950 border border-mono-500',
    4: 'bg-mono-700 text-mono-100 border border-mono-700',
    5: 'bg-mono-900 text-mono-300 border border-mono-600',
  };
  return colors[tier];
}

export function getSiteStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    active: 'ACTIVE',
    monitoring: 'MONITORING',
    remediation: 'REMEDIATION',
    closure_pending: 'CLOSURE PENDING',
    closed: 'CLOSED',
    archived: 'ARCHIVED',
  };
  return labels[status] || status.toUpperCase();
}

// ── Concentration Prediction Helpers ────────────────────────────────────

export function getExceedanceColor(probability: number): string {
  if (probability < 0.3) return 'text-green-400 bg-green-500/20 border-green-500/30';
  if (probability < 0.7) return 'text-amber-400 bg-amber-500/20 border-amber-500/30';
  return 'text-red-400 bg-red-500/20 border-red-500/30';
}

export function getRiskScoreColor(score: number): string {
  if (score >= 70) return 'text-red-500';
  if (score >= 45) return 'text-red-400';
  if (score >= 25) return 'text-amber-400';
  return 'text-green-400';
}

export function getRiskScoreBarColor(score: number): string {
  if (score >= 70) return 'bg-red-600';
  if (score >= 45) return 'bg-red-500';
  if (score >= 25) return 'bg-amber-500';
  return 'bg-green-500';
}

export function getTrendColor(trend: string): string {
  if (trend === 'decreasing' || trend === 'improving') return 'text-green-400';
  if (trend === 'stable' || trend === 'mixed') return 'text-amber-400';
  return 'text-red-400';
}

export function formatConcentration(value: number | null | undefined): string {
  if (value == null) return '-';
  if (value < 0.001) return value.toExponential(2);
  if (value < 1) return value.toFixed(4);
  if (value < 100) return value.toFixed(2);
  return formatNumber(value, 1);
}

export function formatQuarters(quarters: number | null | undefined): string {
  if (quarters == null) return '>10 yr';
  if (quarters <= 4) return `${quarters}Q (~${Math.round(quarters * 3)}mo)`;
  const years = (quarters / 4).toFixed(1);
  return `${quarters}Q (~${years} yr)`;
}

/**
 * Returns analyte severity (1=most severe, 4=least severe) based on MCL.
 * Benzene (MCL=0.005 mg/L) is far more restrictive than xylenes (MCL=10 mg/L).
 */
export function getAnalyteSeverity(analyteCode: string): number {
  const code = analyteCode.toUpperCase();
  if (code === 'B' || code === 'BENZENE') return 1;
  if (code === 'T' || code === 'TOLUENE') return 2;
  if (code === 'E' || code === 'ETHYLBENZENE') return 3;
  if (code === 'X' || code === 'XYLENES' || code === 'TOTAL XYLENES') return 4;
  if (code.includes('BTEX')) return 2;
  if (code.includes('C6') || code.includes('TPH')) return 3;
  return 3; // default for unknown analytes
}

export function getSiteStatusColor(status: string): string {
  // Monochrome styling
  const colors: Record<string, string> = {
    active: 'bg-mono-800 text-mono-200 border border-mono-600',
    monitoring: 'bg-mono-700 text-mono-200 border border-mono-500',
    remediation: 'bg-mono-600 text-mono-100 border border-mono-400',
    closure_pending: 'bg-mono-500 text-mono-950 border border-mono-400',
    closed: 'bg-mono-300 text-mono-950 border border-mono-200',
    archived: 'bg-mono-900 text-mono-400 border border-mono-700',
  };
  return colors[status] || 'bg-mono-800 text-mono-300 border border-mono-600';
}
